import React, { useEffect, useMemo } from 'react';

import { AppState, useDispatch, useSelector } from '@tmapy/redux';
import type { GeoJSONFeature } from '@tmapy/mapcore';
import type { LayerState, MapState } from '@tmapy/beetlejuice-utils';
import {
  actionSetExtent,
  actionSetMapState,
  actionSetMapDefaultState,
  getFeaturesExtent,
  MapProvider,
  MapView,
  Layer,
} from '@tmapy/mapcore';

import { actionSetPredefinedAreasFeatures } from '../actions';

type MapWithAreasProps = {
  state: MapState;
  features?: GeoJSONFeature[];
  extent?: number[];
  children?: React.ReactNode;
};

export const MapWithAreas: React.FC<MapWithAreasProps> = ({
  state,
  features,
  children,
  extent,
}) => {
  const dispatch = useDispatch();
  const mapCore = useSelector((state: AppState) => state.mapCore);
  const providerProps = { ...mapCore.view, dispatch };

  // When the component goes to be unmounted
  useEffect(() => {
    dispatch(actionSetMapState(state));

    return () => {
      // clear state
      dispatch(actionSetMapDefaultState());
    };
  }, []);

  const featuresExtent = useMemo(() => getFeaturesExtent(features ?? []), [features]);
  useEffect(() => {
    featuresExtent && dispatch(actionSetExtent(featuresExtent));
  }, [featuresExtent, dispatch]);

  useEffect(() => {
    extent && dispatch(actionSetExtent(extent));
  }, [extent, dispatch]);

  useEffect(() => {
    dispatch(actionSetPredefinedAreasFeatures(features ?? []));
  }, [dispatch, features]);

  return (
    <MapProvider {...providerProps}>
      <MapView />
      {mapCore.layers.map((layer: LayerState) => (
        <Layer key={layer.id} {...layer} dispatch={dispatch} />
      ))}
      {children}
    </MapProvider>
  );
};
